import { AVAILABILITY_FILTER_KEY_V2 } from 'elements/hardwareFilter/stockAvailability/AvailabilityFilterNodeFactoryV2'
import useHardwareSelectorFetchEngine from 'elements/hardwareSelectorV2/fetch/useHardwareSelectorFetchEngine'
import { Grid, styled, Typography } from 'opensolar-ui'
import { useMemo, useRef, useState } from 'react'
import type { ComponentTypesV2 } from 'types/selectComponent'
import ShopPagination from '../catalogv3/ShopPagination'
import { majorComponentCategories, otherCategories } from '../configs'
import ProductGrid from '../home/ProductGrid'

const componentTypes: ComponentTypesV2[] = [...majorComponentCategories, ...otherCategories] as ComponentTypesV2[]

const PageContainer = styled('div')({
  padding: '24px',
  minHeight: '100vh',
  background: '#F6F9FC',
})

const HeaderContainer = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '16px',
})

const FilterChip = styled('button')<{ active: boolean }>(({ active }) => ({
  padding: '6px 16px',
  borderRadius: '20px',
  border: active ? '2px solid #1751D0' : '1px solid #ccc',
  background: active ? '#EBF0FA' : '#fff',
  color: active ? '#1751D0' : '#333',
  cursor: 'pointer',
  fontWeight: active ? 600 : 400,
  fontSize: '14px',
  transition: 'all 0.15s',
}))

const PAGE_SIZE = 20

const AllDealsPage = () => {
  const [selectedDistributor, setSelectedDistributor] = useState<string | null>(null)
  const resultTotal = useRef(0)

  const defaultFilterValues = useMemo(
    () => ({
      [AVAILABILITY_FILTER_KEY_V2]: 'true',
      has_deals: 'true',
    }),
    []
  )

  const { componentsData, loading, total, page, setPage } = useHardwareSelectorFetchEngine({
    componentTypes,
    defaultFilterValues,
    limit: PAGE_SIZE,
    sortBy: '_distributor_count',
  })

  if (total) {
    resultTotal.current = total
  }

  const { productsWithDeals, distributorNames } = useMemo(() => {
    if (!componentsData?.length) return { productsWithDeals: [], distributorNames: [] }

    const distNames = new Map<string, string>()
    for (const component of componentsData) {
      for (const entry of component.ordering_v2 || []) {
        if (entry.deals?.length && !distNames.has(entry.distributor)) {
          distNames.set(entry.distributor, entry.distributor_name || entry.distributor)
        }
      }
    }

    let filtered = componentsData
    if (selectedDistributor) {
      filtered = componentsData.filter((component: any) =>
        component.ordering_v2?.some(
          (entry: any) => entry.distributor === selectedDistributor && entry.deals?.length > 0
        )
      )
    }

    filtered = filtered.map((f) => ({
      ...f,
      ordering_v2: f.ordering_v2?.filter((o: any) => o.deals?.length),
    }))

    return {
      productsWithDeals: filtered,
      distributorNames: Array.from(distNames.entries()).map(([key, name]) => ({ key, name })),
    }
  }, [componentsData, selectedDistributor])

  return (
    <PageContainer>
      <HeaderContainer>
        <div>
          <Typography textVariant="h4">All Deals</Typography>
          <Typography textVariant="body2">
            {loading ? 'Loading...' : `${resultTotal.current} ${resultTotal.current === 1 ? 'result' : 'results'}`}
          </Typography>
        </div>
      </HeaderContainer>

      {distributorNames.length > 1 && (
        <Grid container spacing={1} style={{ marginBottom: '16px' }}>
          <Grid item>
            <FilterChip active={selectedDistributor === null} onClick={() => setSelectedDistributor(null)}>
              All
            </FilterChip>
          </Grid>
          {distributorNames.map((dist) => (
            <Grid item key={dist.key}>
              <FilterChip
                active={selectedDistributor === dist.key}
                onClick={() => setSelectedDistributor(selectedDistributor === dist.key ? null : dist.key)}
              >
                {dist.name}
              </FilterChip>
            </Grid>
          ))}
        </Grid>
      )}

      <ShopPagination
        page={page}
        limit={PAGE_SIZE}
        total={resultTotal.current}
        onNexPage={(nextPage) => setPage(nextPage)}
        onPreviousPage={(previousPage) => setPage(previousPage)}
        loading={loading}
      />

      <ProductGrid carouselMode={false} componentsData={productsWithDeals} loading={loading} pageSize={PAGE_SIZE} />

      <ShopPagination
        page={page}
        limit={PAGE_SIZE}
        total={resultTotal.current}
        onNexPage={(nextPage) => setPage(nextPage)}
        onPreviousPage={(previousPage) => setPage(previousPage)}
        loading={loading}
      />
    </PageContainer>
  )
}

export default AllDealsPage
